import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, getDoc, query, where, doc } from 'firebase/firestore';
import { firestore, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { calculateRank } from '../utils/rankUtils';
import { normalizePuzzleUrl } from '../utils/urlUtils';
import './TeamDashboard.css';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SponsorLogos from './SponsorLogos';
import { resolveDashboardEventId } from '../utils/activeEvent';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import { getPublicSiteMarketingConfig, joinMarketingUrl } from '../utils/publicSiteConfig';
import { getEventEditionYear } from '../utils/eventRouting';
import { buildTeamPhotoUrlMap, teamPhotoCssObjectPosition } from '../utils/teamPhotoPublic';

const getEventPuzzleMetaFn = httpsCallable(functions, 'getEventPuzzleMeta');

const TeamDashboard = () => {
  const [activeEvent, setActiveEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [scores, setScores] = useState([]);
  const [bonusPuzzles, setBonusPuzzles] = useState([]);
  const [bonusScores, setBonusScores] = useState([]);
  const [teamId] = useState(localStorage.getItem('teamId'));
  const [teamData, setTeamData] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [marketingHomeUrl, setMarketingHomeUrl] = useState('');
  const [marketingRulesUrl, setMarketingRulesUrl] = useState('https://perrisfreshmeet.com/rules');
  const [marketingMapUrl, setMarketingMapUrl] = useState('https://perrisfreshmeet.com/map');
  const [memoryTeamMeta, setMemoryTeamMeta] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('event');
  const yearParam = searchParams.get('year');

  const teamPhotoPublicMap = useMemo(
    () => buildTeamPhotoUrlMap(memoryTeamMeta),
    [memoryTeamMeta]
  );

  useEffect(() => {
    const fetchData = async () => {
      // Fetch resolved public dashboard event (?event= and ?year= supported)
      const id = await resolveDashboardEventId({ eventIdParam, editionYearParam: yearParam });
      if (!id) return;
      const eventSnap = await getDoc(doc(firestore, 'events', id));
      if (!eventSnap.exists()) return;
      const event = { id: eventSnap.id, ...eventSnap.data() };
      setActiveEvent(event);

      const metaSnap = await getDoc(doc(firestore, 'eventMemoryMeta', event.id));
      setMemoryTeamMeta(metaSnap.exists() ? metaSnap.data() : null);

      // Fetch teams for the event
      const teamQuery = query(
        collection(firestore, 'teams'),
        where('eventId', '==', event.id)
      );
      const teamSnap = await getDocs(teamQuery);
      const allTeams = teamSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setTeams(allTeams);

      // Find current team data
      const currentTeam = allTeams.find((t) => t.id === teamId);
      setTeamData(currentTeam);

      // Fetch sky scores
      const scoreQuery = query(
        collection(firestore, 'scores'),
        where('eventId', '==', event.id)
      );
      const scoreSnap = await getDocs(scoreQuery);
      setScores(scoreSnap.docs.map((doc) => doc.data()));

      // Fetch bonus puzzles (via Cloud Function) and bonus scores in parallel
      const [puzzleResult, bonusScoreSnap] = await Promise.all([
        getEventPuzzleMetaFn({eventId: event.id}),
        getDocs(query(collection(firestore, 'bonusScores'), where('eventId', '==', event.id))),
      ]);
      setBonusPuzzles(puzzleResult.data.puzzles || []);
      setBonusScores(bonusScoreSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      const sponsorListRaw = await fetchSponsorsMergedForEvent(event.id);
      const sponsorList = sponsorListRaw
        .filter((s) => !s.hidden && (s.type || 'none') !== 'none' && (s.showOnDashboard !== false));
      sponsorList.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      setSponsors(sponsorList);
    };

    fetchData();
  }, [teamId, eventIdParam, yearParam]);

  useEffect(() => {
    let cancelled = false;
    getPublicSiteMarketingConfig().then((cfg) => {
      if (cancelled) return;
      const base = cfg.marketingSiteBaseUrl || '';
      setMarketingHomeUrl(base || 'https://perrisfreshmeet.com');
      setMarketingRulesUrl(joinMarketingUrl(base, cfg.marketingPaths?.rules || '/rules'));
      setMarketingMapUrl(joinMarketingUrl(base, cfg.marketingPaths?.map || '/map'));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Compute global rankings for all teams
  const rankedTeams = calculateRank(teams, scores, bonusScores, bonusPuzzles);

  // If the current teamData isn't set or not in the ranked list, show loading
  if (!teamData) return <p>Loading team data...</p>;
  const currentTeamGlobal = rankedTeams.find((t) => t.id === teamData.id);
  if (!currentTeamGlobal) return <p>Loading team data...</p>;

  // Filter teams by the same division
  const divisionTeams = teams.filter(
    (t) => t.division === currentTeamGlobal.division
  );
  const rankedTeamsByDivision = calculateRank(
    divisionTeams,
    scores,
    bonusScores,
    bonusPuzzles
  );
  const currentTeamDivisionRank = rankedTeamsByDivision.find(
    (t) => t.id === currentTeamGlobal.id
  )?.rank;

  const editionYear = activeEvent ? getEventEditionYear(activeEvent) : null;
  const scheduleHref =
    editionYear != null
      ? `/${editionYear}/schedule`
      : activeEvent?.id
        ? `/schedule?event=${encodeURIComponent(activeEvent.id)}`
        : '/schedule';
  const eventQs = activeEvent?.id ? `?event=${encodeURIComponent(activeEvent.id)}` : '';

  // Destructure relevant fields
  const {
    skyScores,
    skySubtotal,
    bonusScores: bScores,
    bonusSubtotal,
    total,
    division,
  } = currentTeamGlobal;

  const myTeamPhotoUrl = teamPhotoPublicMap[teamData.id] || '';

  return (
    <div className="team-dashboard">
      {/* Header / Team Info */}
      <div className="team-dashboard-header">
        <img
          src="/fresh-meet-logo.png"
          alt="Fresh Meet Logo"
          className="fresh-meet-logo"
          onClick={() => navigate('/')}
        />
        <h1 className="team-title">
          Team {teamData.teamNumber} - {teamData.teamName}
        </h1>

        {myTeamPhotoUrl ? (
          <div className="team-dashboard-team-photo-wrap">
            <img
              src={myTeamPhotoUrl}
              alt=""
              className="team-dashboard-team-photo"
              style={{ objectPosition: teamPhotoCssObjectPosition(teamData.id, memoryTeamMeta) }}
            />
          </div>
        ) : null}

        <div className="division-rank-row">
          <p className="division-info">
            <strong>Division:</strong> {division}
          </p>
          <p className="rank-info">
            <strong>Rank:</strong> {currentTeamDivisionRank}
          </p>
        </div>

        {/* Make total score larger */}
        <p className="score-info total-score">
          <strong>Total Score:</strong> {total}
        </p>
        <nav className="dashboard-rules-buttons" aria-label="Rules and links">
          {activeEvent?.showLeaderboardOnHome && (
            <Link to={`/leaderboard${eventQs}`} className="dashboard-btn dashboard-btn-leaderboard">Leaderboard</Link>
          )}
          <Link to={`/what-can-i-win${eventQs}`} className="dashboard-btn">What can I win?</Link>
          <a href={scheduleHref} target="_blank" rel="noopener noreferrer" className="dashboard-btn dashboard-btn-schedule">Schedule</a>
          <a href={marketingRulesUrl} target="_blank" rel="noopener noreferrer" className="dashboard-btn dashboard-btn-rules">Rules</a>
          <a href={marketingMapUrl} target="_blank" rel="noopener noreferrer" className="dashboard-btn dashboard-btn-map">Map</a>
          <a href={marketingHomeUrl || '/'} target="_blank" rel="noopener noreferrer" className="dashboard-btn dashboard-btn-marketing">Main Site</a>
          {activeEvent?.showMorningLoadOnHome !== false && (
            <Link to={`/morning-load-order${eventQs}`} className="dashboard-btn dashboard-btn-morning-load">Morning load order</Link>
          )}
        </nav>
      </div>

      {/* Main Table — desktop: Rank & Total first (sticky), then Sky & Bonus */}
      <div className="table-container team-dashboard-table-wrap">
        <table className="team-dashboard-table">
          <thead>
            <tr>
              <th rowSpan="2" className="td-sticky-rank">Rank</th>
              <th rowSpan="2" className="td-sticky-total">Total</th>
              <th colSpan={skyScores.length + 1}>Sky Scores</th>
              <th colSpan={bScores.length + 1}>Bonus Scores</th>
            </tr>
            <tr>
              {skyScores.map((_, idx) => (
                <th key={`sky-header-${idx}`}>{idx + 1}</th>
              ))}
              <th>∑</th>
              {bScores.map((_, idx) => (
                <th key={`bonus-header-${idx}`}>{idx + 1}</th>
              ))}
              <th>∑</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="td-sticky-rank"><strong>{currentTeamDivisionRank}</strong></td>
              <td className="td-sticky-total"><strong>{total}</strong></td>
              {skyScores.map((score, idx) => (
                <td key={`sky-score-${idx}`} className={score ? 'sky-scored' : ''}>{score}</td>
              ))}
              <td><strong>{skySubtotal}</strong></td>
              {bScores.map((score, idx) => {
                const puzzle = bonusPuzzles[idx];
                const isAvailable = !!puzzle?.onDashboard;
                const rawUrl = isAvailable && ((puzzle?.dashboardUrl || '').trim() || (puzzle?.publicCode ? `/p/${puzzle.publicCode}` : null));
                const interactUrl = rawUrl ? normalizePuzzleUrl(rawUrl) : null;
                const content = score > 0 ? score : '—';
                return (
                  <td
                    key={`bonus-score-${idx}`}
                    className={score > 0 ? 'bonus-scored' : ''}
                    data-tooltip={score > 0 ? (puzzle?.title || '') : ''}
                  >
                    {interactUrl ? (
                      <a href={interactUrl} target="_blank" rel="noopener noreferrer" className="bonus-score-link">
                        {content}
                      </a>
                    ) : (
                      content
                    )}
                  </td>
                );
              })}
              <td><strong>{bonusSubtotal}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile: Rank & Total first, then stacked Sky + Bonus blocks */}
      <div className="team-dashboard-scores-mobile">
        <div className="mobile-rank-total-bar">
          <div className="mobile-rank-total-item">
            <span className="mobile-label">Rank</span>
            <span className="mobile-value">{currentTeamDivisionRank}</span>
          </div>
          <div className="mobile-rank-total-item mobile-total">
            <span className="mobile-label">Total Score</span>
            <span className="mobile-value">{total}</span>
          </div>
        </div>
        <section className="score-block score-block-sky">
          <h4>Sky Scores</h4>
          <div className="score-block-grid">
            {skyScores.map((score, idx) => (
              <div key={`sky-m-${idx}`} className={`score-cell ${score ? 'score-cell-scored' : ''}`}>
                <span className="score-label">{idx + 1}</span>
                <span className="score-value">{score || '—'}</span>
              </div>
            ))}
            <div className="score-cell score-cell-total">
              <span className="score-label">∑</span>
              <span className="score-value">{skySubtotal}</span>
            </div>
          </div>
        </section>
        <section className="score-block score-block-bonus">
          <h4>Bonus Scores</h4>
          <div className="score-block-grid">
            {bScores.map((score, idx) => {
              const puzzle = bonusPuzzles[idx];
              const isAvailable = !!puzzle?.onDashboard;
              const rawUrl = isAvailable && ((puzzle?.dashboardUrl || '').trim() || (puzzle?.publicCode ? `/p/${puzzle.publicCode}` : null));
              const interactUrl = rawUrl ? normalizePuzzleUrl(rawUrl) : null;
              const cellClass = `score-cell ${score > 0 ? 'score-cell-scored' : ''} ${interactUrl ? 'score-cell-link' : ''}`;
              const label = idx + 1;
              const value = score > 0 ? score : '—';
              const inner = (
                <>
                  <span className="score-label">{label}</span>
                  <span className="score-value">{value}</span>
                </>
              );
              return interactUrl ? (
                <a key={`bonus-m-${idx}`} href={interactUrl} target="_blank" rel="noopener noreferrer" className={cellClass}>
                  {inner}
                </a>
              ) : (
                <div key={`bonus-m-${idx}`} className={cellClass}>{inner}</div>
              );
            })}
            <div className="score-cell score-cell-total">
              <span className="score-label">∑</span>
              <span className="score-value">{bonusSubtotal}</span>
            </div>
          </div>
        </section>
      </div>

      {sponsors.length > 0 && <SponsorLogos sponsors={sponsors} variant="subtle" />}
      <div className="team-dashboard-footer">
        <button onClick={() => navigate('/')} className="full-dashboard-button">
          Main Page
        </button>
      </div>
    </div>
  );
};

export default TeamDashboard;
